/**
 * Sales feature · TypeScript types (rev-10 frozen).
 *
 * Mirrors the sales-service backend DTOs + master-data activation DTOs.
 * Types here are the wire shapes · component-internal form types use the `*FormData` variants.
 */

// ─── Activity types ─────────────────────────────────────────────────

export type LeadActivityType =
  | 'CALL'
  | 'EMAIL'
  | 'MEETING'
  | 'WHATSAPP'
  | 'NOTE'
  | 'SITE_VISIT'
  | 'DEMO'

/**
 * Every type the activity LOG can hand back on a lead — a superset of [LeadActivityType].
 *
 * The composer offers the seven a rep logs by hand. The log holds fifteen, because the schedule
 * module, the action-item queue and the AI service all write into the same collection against
 * whatever context they are working on. A lead's timeline can therefore contain a `TASK` or a
 * `PAYMENT_CHASE` that no dropdown here ever offered.
 *
 * Kept separate from [LeadActivityType] rather than merged into it: widening the composer's union
 * would put `FOCUS` and `PERSONAL` in the rep's "log an activity" dropdown, which is not what any
 * of them mean on a lead.
 */
export type ActivityLogType =
  | LeadActivityType
  | 'TASK'
  | 'PAYMENT_CHASE'
  | 'PAYMENT_NOTE'
  | 'STANDUP'
  | 'FOCUS'
  | 'TRAVEL'
  | 'PERSONAL'
  | 'OTHER'

export type ActivityLinkedEntityType = 'LEAD' | 'OPPORTUNITY' | 'CUSTOMER'

export type ActivityOutcome =
  | 'POSITIVE'
  | 'NEUTRAL'
  | 'NEGATIVE'
  | 'NO_RESPONSE'
  | 'CLOSED_WON'
  | 'CLOSED_LOST'
  // Call outcomes (sales-scoreboard E10) — mirror master-data's enum by name.
  | 'CONNECTED'
  | 'NO_ANSWER'
  | 'CALL_BACK'
  | 'NOT_INTERESTED'

// ─── Embedded types ────────────────────────────────────────────────

export interface ContactApi {
  contactId: string
  name: string
  emails: string[]
  phones: string[]
  whatsappNumber?: string
  role?: string
  department?: string
  isPrimary: boolean
  isActive: boolean
  notes?: string
}

export interface ContactSummary {
  contactId: string
  name: string
  role?: string
  isActive: boolean
}

export interface StageChangeOutcome {
  fromStage: string
  toStage: string
}

export interface StageHistoryEntryResponse {
  fromStage: string
  toStage: string
  changedAt: string
  changedBy: string
  changedByName?: string
  linkedActivityId?: string | null
  reason?: string | null
}

export interface AssignmentHistoryEntryResponse {
  fromUserId: string
  fromUserName?: string
  toUserId: string
  toUserName?: string
  changedAt: string
  changedBy: string
  changedByName?: string
  reason?: string | null
}

// ─── Lead ──────────────────────────────────────────────────────────


// ─── Urgency (Task 1) ──────────────────────────────────────────────

/**
 * A level a PERSON assigned to a lead. Automatic is the absence of one, never a fifth value —
 * see the backend `LeadAssignedUrgency` for why that distinction is load-bearing.
 */
export type AssignedUrgency = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW'

/** The calculated ladder. Same four words; a different question. */
export type EffectiveUrgency = 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW'

/** Did a person choose this level, or did the system calculate it? Always shown, never implied. */
export type UrgencyMode = 'AUTOMATIC' | 'MANUAL'

/**
 * Whether the server could answer exactly.
 *
 * `CHAIN_UNAVAILABLE` is not an error: the list still renders. It means an AUTOMATIC lead's level
 * is unknown rather than merely lower, so the badge shows a neutral `Unavailable · Automatic`
 * instead of a confident level the server could not stand behind. An assigned lead stays exact.
 */
export type UrgencyResolution = 'COMPLETE' | 'CHAIN_UNAVAILABLE'

/**
 * Urgency metadata carried on every lead list row and on lead detail.
 *
 * All optional: an older server omits them entirely, and every consumer must render without a
 * badge rather than break. Terminal leads legitimately carry nulls too.
 */
export interface LeadUrgencyFields {
  focusState?: string
  focusLabel?: string
  assignedUrgency?: AssignedUrgency | null
  dynamicUrgency?: EffectiveUrgency | null
  effectiveUrgency?: EffectiveUrgency | null
  urgencyMode?: UrgencyMode
  urgencyResolution?: UrgencyResolution
}

export interface LeadListApiItem extends LeadUrgencyFields {
  leadId: string
  name: string
  primaryContactName: string
  primaryContactEmail?: string
  /** Job title / designation of the primary contact — hover card (Kiko Leads #10). */
  primaryContactRole?: string
  /** First phone of the primary contact — hover card (Kiko Leads #10). */
  primaryContactPhone?: string
  contactCount: number
  customerId: string
  source: string
  stage: string
  assignedToUserId: string
  assignedToUserName?: string
  value?: number
  currency?: string
  expectedCloseDate?: string
  isActive: boolean
  lastActivityAt?: string
  /** Number of logged activities ("touches"). Optional until the backend ships it. */
  activityCount?: number
  tags: string[]
  /** Set while a lead is inside its shelf-life warning window (Kiko Leads #3). */
  shelfLifeWarnedAt?: string | null
  /** Who the lead was released from, while it sits unclaimed in the pool. */
  releasedFromUserId?: string | null
  createdAt: string
  updatedAt: string
}

// ─── Lead Pool (sales-lead-pool-cleanup §4.8) ──────────────────────

/**
 * PROVISIONAL = the company has no customer type yet (owned or unowned).
 * READY       = typed and unowned — anyone may claim it.
 */
export type PoolStatus = 'PROVISIONAL' | 'READY'

/** The two values a person may set. `UNRECOGNIZED` is what "not set" looks like on the wire. */
export type SettableCustomerType = 'DIRECT_CUSTOMER' | 'FREIGHT_FORWARDER'

/**
 * One row of GET /api/v1/leads/pool — the list-row fields plus the pool-only ones.
 *
 * The contract says "the existing LeadResponse fields PLUS …". The list endpoint returns the
 * slimmer list row (primaryContact* rather than `contacts[]`), so both contact shapes are
 * accepted and the page reads whichever arrives.
 */
export interface PoolLeadItem extends Omit<LeadListApiItem, 'primaryContactName' | 'contactCount'> {
  primaryContactName?: string
  contactCount?: number
  contacts?: ContactApi[]
  customerType?: string | null
  customerLifecycleStage?: string | null
  poolStatus: PoolStatus
  hasDoneBusiness: boolean
  engaged: boolean
  /** Null when unowned. */
  ownerName?: string | null
  createdBy?: string | null
  /** "July 2026 data migration" for the July rebuild, "System" for system/agent creators. */
  createdByName?: string | null
  /**
   * The opportunity's notes — the pool row carries every LeadResponse field, notes included
   * (`LeadPoolRowResponse.notes`). Holds the auto-written customer briefs (E1/E7).
   */
  notes?: string | null
  /** See `LeadApi.lastInboundAt`. */
  lastInboundAt?: string | null

  // ── Live dispatch fields (sales-lead-pool-dispatch §3). Optional: an older server omits them,
  //    and the page then renders the row exactly as the plain register did. ──
  /** Server-computed (L1). HOT = enquiry or quote reply within `hotHours`. */
  temperature?: PoolTemperature | null
  /** Plain-language reasons for the temperature, e.g. "Website enquiry 4 min ago". */
  temperatureReasons?: string[] | null
  /** Route / service line from the latest case brief. */
  briefHeadline?: string | null
  /** Deterministic type guess for an untyped company (L2). */
  suggestedType?: SettableCustomerType | null
  /** HOT and still unclaimed past the claim SLA (L3). */
  claimOverdue?: boolean | null
  /** `lastInboundAt`, else `createdAt` — the clock the claim timer counts from. */
  waitingSince?: string | null
}

// ─── Lead Pool · live dispatch (sales-lead-pool-dispatch §3) ───────

export type PoolTemperature = 'HOT' | 'WARM' | 'COLD'

/** The `queue` query param. `ALL` = no queue filter. */
export type PoolQueueKey = 'WEBSITE' | 'EMAIL' | 'RETURNING' | 'IMPORTED' | 'REFERRAL' | 'ALL'

export interface PoolQueueCount {
  key: PoolQueueKey | string
  label: string
  count: number
  newToday: number
}

/** GET /api/v1/leads/pool/pulse — visible-set aware. */
export interface PoolPulse {
  newLastHour: number
  hotUnclaimed: number
  /** Null when nothing hot is waiting. */
  oldestHotWaitingMinutes: number | null
  claimedToday: number
  /** Null when nobody has claimed today. */
  medianClaimMinutesToday: number | null
  /** Per queue: unowned HOT + WARM leads (`count`) and today's new ones. */
  queues: PoolQueueCount[]
  hotUnclaimedForBadge: number
  /** Server-computed: the caller manages the pool (Team dispatch, exempt from the holding cap). */
  isManager?: boolean | null
  /**
   * Per-caller desk figures. NOT in the §3 contract — read only if the server sends them; the
   * "Your desk" card falls back to what the contract does give (policy cap, leaderboard).
   */
  myUntouchedClaims?: number | null
  myClaimedToday?: number | null
  myFirstCallsDue?: number | null
}

export type RepLoad = 'FREE' | 'ROOM' | 'FULL'

export interface DispatchRep {
  userId: string
  /** Nullable on the wire — display `name ?? userId` (see `repName`). */
  name: string | null
  holding: number
  untouched: number
  /** Untouched claims taken from the pool — what the holding cap (and `load`) counts. */
  untouchedFromPool?: number | null
  claimedToday: number
  firstCallMedianMinutes: number | null
  firstCallOverdue: number | boolean | null
  load: RepLoad
}

/** The waiting row's suggested owner — an object or a bare user id, both accepted. */
export type SuggestedOwner = { userId: string; name?: string | null } | string | null

export interface DispatchWaitingItem extends PoolLeadItem {
  suggestedOwner?: SuggestedOwner
}

/** GET /api/v1/leads/pool/dispatch (managers). */
export interface PoolDispatch {
  reps: DispatchRep[]
  waiting: DispatchWaitingItem[]
  overdueCount: number
}

export interface AutoDistributeAssignment {
  leadId: string
  leadName?: string | null
  userId: string
  userName?: string | null
}

export interface AutoDistributeSkip {
  leadId: string
  leadName?: string | null
  /** Human text: the server's `message`, else its `reason` code. */
  reason: string
}

/** POST /api/v1/leads/pool/claim-next, normalised (see `normalizeClaimNext`). */
export interface ClaimNextResult {
  lead: PoolLeadItem
  appliedType: SettableCustomerType | null
  /** True when the server set the suggested type on an untyped company to make the claim. */
  appliedSuggested: boolean
}

/** POST /api/v1/leads/pool/auto-distribute, normalised (see `normalizeAutoDistribute`). */
export interface AutoDistributeResult {
  assigned: AutoDistributeAssignment[]
  skipped: AutoDistributeSkip[]
}

/** One row of GET /api/v1/leads/pool/leaderboard, normalised (see `normalizeLeaderboard`). */
export interface LeaderboardEntry {
  userId: string
  name: string
  claimsToday: number
  medianClaimMinutes: number | null
}

/** SalesSettings.dispatchPolicy — GET/PUT /api/v1/sales-settings/dispatch-policy. */
export interface DispatchPolicyApi {
  hotHours: number
  warmDays: number
  claimSlaMinutes: number
  firstCallSlaMinutes: number
  holdingCap: number
  holdingCapEnabled: boolean
  autoAssignEnabled: boolean
}

export const DEFAULT_DISPATCH_POLICY: DispatchPolicyApi = {
  hotHours: 24,
  warmDays: 7,
  claimSlaMinutes: 15,
  firstCallSlaMinutes: 60,
  holdingCap: 10,
  holdingCapEnabled: true,
  autoAssignEnabled: false,
}

export interface LeadPoolSummary {
  provisionalOwnedByMe: number
  provisionalVisible: number
  readyToClaim: number
}

export interface BulkSetCustomerTypeResult {
  updated: string[]
  failed: Array<{ leadId: string; errorCode?: string | null; message?: string | null }>
}

/**
 * Alias for the fields the focus engine reads off a lead. Kept as a named type so
 * focus.ts (which imports LeadListItem) resolves to the real list-item shape instead
 * of a dangling import.
 */
export type LeadListItem = LeadListApiItem

// ─── Lost-case hand-over (sales-lost-case-handover §3) ─────────────

/** Where a lost case stands in the head's review (LC3). */
export type LostReviewStatus = 'PENDING' | 'REVIVED' | 'DISQUALIFIED' | 'RETURNED_TO_POOL'

export interface LostReviewApi {
  /** A `LostReviewStatus`; typed loosely so an unknown value from a newer server still renders. */
  status: LostReviewStatus | string
  decidedBy?: string | null
  decidedByName?: string | null
  decidedAt?: string | null
  note?: string | null
}

/**
 * The lost fields on Lead / pool / list responses (LC3). All optional: a case lost before the
 * hand-over shipped, an older server, and every open opportunity carry none of them.
 */
export interface LeadLostFields {
  lostByUserId?: string | null
  lostByName?: string | null
  lostAt?: string | null
  lostReason?: string | null
  lostReview?: LostReviewApi | null
  /** READ_ONLY co-ownership only (LC5): who may still open the case, read-only. */
  coOwnerUserIds?: string[] | null
  /** Not in the §3 contract — read only if the server sends it; else derived from stageHistory. */
  lostFromStage?: string | null
}

/** NONE = the rep who lost it no longer sees it; READ_ONLY = they may still open it (LC1/LC5). */
export type CoOwnershipMode = 'NONE' | 'READ_ONLY'

/** GET/PUT /api/v1/sales-settings/lost-case-handover. */
export interface LostCaseHandoverApi {
  enabled: boolean
  ownerUserId: string | null
  ownerName?: string | null
  coOwnership: CoOwnershipMode
}

/** GET /api/v1/leads/lost-cases/summary (leaders) — the tab badge is `pending`. */
export interface LostCasesSummary {
  pending: number
  revived: number
  disqualified: number
  returnedToPool: number
}

/**
 * One row of GET /api/v1/leads/lost-cases: LeadResponse fields + the lost fields + company name,
 * value, lastActivityAt. Both contact shapes are accepted, as on the pool rows.
 */
export interface LostCaseItem extends LeadLostFields {
  leadId: string
  name: string
  customerId?: string | null
  companyName?: string | null
  customerName?: string | null
  primaryContactName?: string | null
  primaryContactEmail?: string | null
  contacts?: ContactApi[] | null
  stage: string
  assignedToUserId?: string | null
  assignedToUserName?: string | null
  value?: number | null
  currency?: string | null
  lastActivityAt?: string | null
  stageHistory?: StageHistoryEntryResponse[] | null
  /** false = archived. The backfill may hand over archived lost cases (listed only on request). */
  isActive?: boolean | null
  createdAt?: string
  updatedAt?: string
}

export interface LeadApi extends LeadUrgencyFields, LeadLostFields {
  leadId: string
  name: string
  customerId: string
  contacts: ContactApi[]
  source: string
  stage: string
  assignedToUserId: string
  assignedToUserName?: string
  value?: number
  currency?: string
  expectedCloseDate?: string
  notes?: string
  tags: string[]
  isActive: boolean
  lastActivityAt?: string
  stageHistory: StageHistoryEntryResponse[]
  assignmentHistory: AssignmentHistoryEntryResponse[]
  createdAt: string
  updatedAt: string
  createdBy: string
  createdByName?: string
  updatedBy: string
  updatedByName?: string
  /** Who the lead was released from, while it sits unclaimed in the pool (reclaim lock). */
  releasedFromUserId?: string | null
  /** Stamped company type, when the server includes it (Lead Pool clean-up). */
  customerType?: string | null
  /** Stamped company lifecycle (LEAD/PROSPECT/ACCOUNT), when the server includes it. */
  customerLifecycleStage?: string | null
  /**
   * When the customer last reached out — the latest enquiry (sales-engagement-capture E4).
   * Optional: absent until the backend ships it, and null for a lead with no recorded enquiry.
   */
  lastInboundAt?: string | null
}

export interface ContactFormData {
  contactId?: string
  name: string
  emails: string[]
  phones: string[]
  whatsappNumber: string
  role: string
  department: string
  isPrimary: boolean
  isActive: boolean
  notes: string
}

export interface LeadFormData {
  name: string
  customerId: string | null
  contacts: ContactFormData[]
  source: string
  assignedToUserId: string
  value: string
  expectedCloseDate: string
  notes: string
  tags: string[]
}

// ─── Activity ──────────────────────────────────────────────────────

export interface ActivityApi {
  activityId: string
  linkedEntityType: ActivityLinkedEntityType
  linkedEntityId: string
  /** The LOG's set, not the composer's — see [ActivityLogType]. */
  type: ActivityLogType
  subject: string
  description?: string
  timestamp: string
  createdBy: string
  createdByName?: string
  createdAt: string
  contactIds: string[]
  contactsInvolved: ContactSummary[]
  resultingStageChange?: StageChangeOutcome | null
  outcome?: ActivityOutcome | null
  nextAction?: string | null
  nextActionDate?: string | null
  metadata: Record<string, string>
  /** See `ApiResponse.warning` — accepted on either the envelope or the activity. */
  warning?: string | null
}

export interface AddActivityPayload {
  type: LeadActivityType
  subject: string
  description?: string
  timestamp?: string
  contactIds?: string[]
  resultingStageChange?: {
    newStage: string
    reason?: string
  } | null
  outcome?: ActivityOutcome | null
  /** Follow-up seed · when set, the backend fires the follow-up-task hook. */
  nextAction?: string | null
  nextActionDate?: string | null
  /**
   * The planned activity this log COMPLETES (Task 1).
   *
   * Without it this path was a lineage dead end: a rep who planned a call and then logged it
   * through the lead form produced two unconnected rows, and the lead's chain depth never grew
   * past 1. Validated server-side — the parent must exist, be readable, share this lead's
   * context and not close a cycle.
   *
   * Left unset by the plain "log something that happened" flows, which complete nothing. The
   * lead surfaces that DO know which planned row is being closed go through
   * `LogActivityDialog`, which carries the id on both the log and its next step.
   */
  followUpOfActivityId?: string
}

// ─── SalesSettings ─────────────────────────────────────────────────

export interface FunnelStageConfigApi {
  stageKey: string
  label: string
  displayOrder: number
  color: string
  isInitialStage: boolean
  isWonTerminal: boolean
  isLostTerminal: boolean
  isActive: boolean
  /** Win probability 0..1 for revenue forecasting. Null → server default. */
  winProbability?: number | null
}

export interface LeadSourceConfigApi {
  sourceKey: string
  label: string
  displayOrder: number
  isActive: boolean
}

/** Outreach cadence policy — drives the Focus Engine. All values are whole days. */
export interface CadencePolicyApi {
  firstTouchDays: number
  touchCadenceDays: number
  coldAfterDays: number
  closingSoonDays: number
}

export const DEFAULT_CADENCE_POLICY: CadencePolicyApi = {
  firstTouchDays: 2,
  touchCadenceDays: 7,
  coldAfterDays: 14,
  closingSoonDays: 7,
}

/**
 * When an unworked opportunity returns to the shared pool (Kiko Leads #3).
 * Two independent clocks: one for "never touched", one for "touched but never progressing".
 */
export interface ShelfLifePolicyApi {
  enabled: boolean
  untouchedDays: number
  noProgressDays: number
  warnDaysBefore: number
}

/** Off by default — this takes leads off people, so a tenant opts in deliberately. */
export const DEFAULT_SHELF_LIFE_POLICY: ShelfLifePolicyApi = {
  enabled: false,
  untouchedDays: 30,
  noProgressDays: 60,
  warnDaysBefore: 3,
}

export interface SalesSettingsApi {
  funnelStages: FunnelStageConfigApi[]
  leadSources: LeadSourceConfigApi[]
  /** Tenant default assignee for auto-created Leads (e.g., ai-py email RFQs). null = unset. */
  defaultLeadOwnerUserId?: string | null
  /** Kiko #34 — this tenant's sales assistants (user ids); they see the "select your own name" note. */
  salesAssistantUserIds?: string[]
  /** Kiko #34 — who each assistant works for: assistant user id → the user ids they help (set by the Admin). */
  salesAssistantWorksFor?: Record<string, string[]>
  cadencePolicy?: CadencePolicyApi
  shelfLifePolicy?: ShelfLifePolicyApi
  /** Lead Pool dispatch rules (sales-lead-pool-dispatch). Read via its own endpoint too. */
  dispatchPolicy?: DispatchPolicyApi
  /** Lost-case hand-over (sales-lost-case-handover LC1). Read via its own endpoint too. */
  lostCaseHandover?: LostCaseHandoverApi
  isCustomized: boolean
  updatedAt?: string
  updatedBy?: string
}

// ─── TagDefinition ─────────────────────────────────────────────────

export interface TagDefinitionApi {
  name: string
  color?: string
  description?: string
  createdBy?: string
  createdAt: string
}

export interface CreateTagDefinitionPayload {
  name: string
  color?: string
  description?: string
}

// ─── Stats (S0) ────────────────────────────────────────────────────

export interface LeadStageStats {
  count: number
  valueSum: number
}

export interface LeadDashboardStats {
  byStage: Record<string, LeadStageStats>
  active: LeadStageStats
  computedAt: string
}

// ─── Customer activation (F10) ─────────────────────────────────────

export interface CustomerFinancialsPayload {
  currency: string
  creditLimit: string
  paymentTerms: string
  paymentTermsDays: number | null
  billingEmail: string
}

export interface ActivateCustomerPayload {
  contractDocumentId: string
  contractEffectiveDate: string
  activationNotes?: string
  financials: CustomerFinancialsPayload
}

// ─── Filters (LeadListPage local state) ────────────────────────────

/** Expected-close presets surfaced in the "More filters" popover. */
export type CloseWindow = 'ALL' | 'OVERDUE' | 'THIS_WEEK' | 'THIS_MONTH' | 'HAS_DATE' | 'NO_DATE'

export interface LeadFilterState {
  search: string
  stage: string | 'ALL'
  source: string | 'ALL'
  /** The linked company's lifecycle (Lead/Prospect/Account); resolved server-side. */
  lifecycle: 'ALL' | CustomerLifecycle
  assignedToUserId: string | 'ALL'
  includeInactive: boolean
  // ── advanced (More filters) ──
  valueMin: number | null
  valueMax: number | null
  closeWindow: CloseWindow
  /** Selected tag keys; matches opportunities having ANY of them. */
  tags: string[]
  /** Only opportunities with no real owner. */
  unassigned: boolean
  /**
   * Effective-urgency filter (Task 1). `ALL` means no filter.
   *
   * Sent to the server and applied BEFORE pagination and before the board's first-100 cap —
   * filtering loaded rows in the browser would omit valid leads and report a wrong total.
   */
  urgency: 'ALL' | EffectiveUrgency
}

/**
 * The pool page's query (GET /api/v1/leads/pool). `ownerId` takes a user id, `me` or `unowned`.
 */
export interface PoolLeadsParams {
  page?: number
  size?: number
  status?: 'ALL' | PoolStatus
  source?: string
  ownerId?: string
  createdBy?: string
  engaged?: boolean
  hasDoneBusiness?: boolean
  search?: string
  sort?: string
  /** Live board (dispatch §3). Omitted = every temperature. */
  temperature?: PoolTemperature
  /** Live board (dispatch §3). Omitted / ALL = every queue. */
  queue?: PoolQueueKey
}

// ─── Transcribe (F11) ──────────────────────────────────────────────

export interface TranscribeResponse {
  text: string
  durationSeconds: number
  traceId?: string | null
}

// ─── Customer lifecycle (the company's status, distinct from the funnel stage) ───
export type CustomerLifecycle = 'LEAD' | 'PROSPECT' | 'ACCOUNT'

// ─── Generic API envelope (mirror existing pattern) ────────────────

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data?: T
  timestamp: string
  errorCode?: string
  details?: unknown
  /**
   * A success with a caveat — e.g. `ACTIVITY_NOT_LOGGED…` when a lost case was handed over but
   * the activity record itself failed to save (sales-lost-case-handover).
   */
  warning?: string | null
}

export interface PaginatedResponse<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
  hasNext: boolean
  hasPrevious: boolean
}

/**
 * Shape of an LLM-extracted Activity draft, as applied to AddActivityForm.
 *
 * Lived in `hooks/useExtractActivity.ts` until 2026-08-15. That hook was deleted — nothing
 * called it; single-shot extraction was superseded by the conversational path
 * (`activity-logging/useActivityConverse`) and AddActivityForm builds its draft inline.
 * Only the shape survived, so it moved here rather than leaving a hook file with no hook.
 */
export interface ExtractedActivityDraft {
  type: LeadActivityType
  subject: string
  description?: string | null
  contactIds: string[]
  outcome?: ActivityOutcome | null
  resultingStage?: string | null
  stageChangeReason?: string | null
  confidence: number
  traceId?: string | null
}
